import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Modal, Spin, Select, Input } from "antd";
import { all_routes } from "../../../router/all_routes";
import { AppDispatch } from "../../../../core/data/redux/store";
import {
  getStaffQuarterDetails,
  getStaffQuarterCurrentPendingRequests,
  updateStaffQuarterCurrentApprover,
  getStaffQuarterAdminPermissions,
} from "../../../../core/data/redux/staffQuarterSlice";
import { getEmployees, getEmployeeProfile } from "../../../../core/data/redux/employeesSlice";
import { formatFlatNoWithBlock } from "../staffQuarterUtils";

type ModuleType = "REGISTRATION" | "WILLINGNESS" | "OCCUPATION" | "VACATION" | "FINANCE";

interface ApproverInfo {
  empId: string;
  name: string;
  designation?: string;
  status?: string;
  stage?: number | string;
  role?: string;
  isAssigned?: boolean;
  approvedAt?: string;
  remarks?: string;
  approvalId?: number | string;
}

const padEmpNo = (v?: string | null): string => {
  const t = (v ?? "").trim();
  if (!t) return "";
  return t.replace(/^0+/, "") || t;
};

const formatDate = (date: any): string => {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return String(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear();
  return `${day} ${month}, ${year}`;
};

const formatDateTime = (date: any): string => {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return String(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${day} ${month}, ${year} ${time}`;
};

const ChangeCurrentApprover: React.FC = () => {
  const routes = all_routes;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  // Search by Employee ID
  const [searchEmpId, setSearchEmpId] = useState<string>("00010212");
  const [searchEmployeeOptions, setSearchEmployeeOptions] = useState<any[]>([]);
  const [searchingApplicant, setSearchingApplicant] = useState<boolean>(false);
  const applicantSearchTimeoutRef = useRef<any>(null);

  // Module State
  const [selectedModule, setSelectedModule] = useState<ModuleType>("VACATION");

  // Request Data & Loading States
  const [fetchingRequest, setFetchingRequest] = useState<boolean>(false);
  const [rawApiResponse, setRawApiResponse] = useState<any>(null);
  const [availableRequestsList, setAvailableRequestsList] = useState<any[]>([]);
  const [activeRequestData, setActiveRequestData] = useState<any>(null);
  const [currentApprover, setCurrentApprover] = useState<ApproverInfo | null>(null);
  const [allApproversList, setAllApproversList] = useState<ApproverInfo[]>([]);

  // New Approver State
  const [newApproverEmpId, setNewApproverEmpId] = useState<string>("");
  const [newApproverDetails, setNewApproverDetails] = useState<any>(null);
  const [newApproverOptions, setNewApproverOptions] = useState<any[]>([]);
  const [searchingNewApprover, setSearchingNewApprover] = useState<boolean>(false);
  const newApproverSearchTimeoutRef = useRef<any>(null);

  // Remarks & Submission State
  const [remarks, setRemarks] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

  // Predefined Remark Suggestions
  const remarkPresets = [
    "Approver on leave / extended absence",
    "Administrative transfer of review responsibility",
    "Reassigned per Competent Authority direction",
    "Workload rebalancing across officers",
    "Immediate approval escalation required",
  ];

  // Fetch admin permissions on mount
  useEffect(() => {
    dispatch(getStaffQuarterAdminPermissions({}));
  }, [dispatch]);

  // Read query params if navigated with pre-filled employee or request info
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const empParam = params.get("empId") || params.get("employeeNumber");
    const mod = params.get("module")?.toUpperCase() as ModuleType;
    if (mod && ["REGISTRATION", "WILLINGNESS", "OCCUPATION", "VACATION", "FINANCE"].includes(mod)) {
      setSelectedModule(mod);
    }
    if (empParam) {
      const padded = padEmpNo(empParam);
      setSearchEmpId(padded);
      handleFetchPendingRequests(padded, mod || selectedModule);
    }
  }, [location.search]);

  // Fetch ongoing pending requests by Employee ID using current-pending-requests
  const handleFetchPendingRequests = async (empIdToSearch?: string, modToSelect?: ModuleType) => {
    const targetEmpId = padEmpNo(empIdToSearch || searchEmpId);
    const mod = modToSelect || selectedModule;

    if (!targetEmpId) {
      toast.warning("Please enter or select a valid Employee ID.");
      return;
    }

    setFetchingRequest(true);
    setRawApiResponse(null);
    setAvailableRequestsList([]);
    setActiveRequestData(null);
    setCurrentApprover(null);
    setAllApproversList([]);
    setNewApproverEmpId("");
    setNewApproverDetails(null);

    try {
      // 1. Call POST /staff-quarters/current-pending-requests with { empId }
      const res = await dispatch(
        getStaffQuarterCurrentPendingRequests({ empId: targetEmpId })
      ).unwrap();

      const root = res?.data || res;
      const dataObj = root?.data || root;
      setRawApiResponse(root);

      let parsedList: any[] = [];

      if (Array.isArray(dataObj?.myPendingRequests)) {
        parsedList = dataObj.myPendingRequests;
      } else if (Array.isArray(root?.myPendingRequests)) {
        parsedList = root.myPendingRequests;
      } else if (Array.isArray(dataObj?.requests)) {
        parsedList = dataObj.requests;
      } else if (Array.isArray(root?.requests)) {
        parsedList = root.requests;
      } else if (Array.isArray(dataObj)) {
        parsedList = dataObj;
      } else if (Array.isArray(root)) {
        parsedList = root;
      } else if (
        dataObj &&
        typeof dataObj === "object" &&
        (dataObj.id ||
          dataObj.registrationId ||
          dataObj.vacationId ||
          dataObj.willingnessId ||
          dataObj.occupationId ||
          dataObj.module ||
          dataObj.vacationIntimationId)
      ) {
        parsedList = [dataObj];
      }

      // If current-pending-requests returned an empty list or specific message, try fallback to getStaffQuarterDetails if ID is present
      if (parsedList.length === 0 && (root?.registrationId || dataObj?.registrationId)) {
        try {
          const detailRes = await dispatch(
            getStaffQuarterDetails({ id: root?.registrationId || dataObj?.registrationId })
          ).unwrap();
          const detailData = detailRes?.data || detailRes;
          if (detailData) parsedList = [detailData];
        } catch (e) {
          // ignore
        }
      }

      setAvailableRequestsList(parsedList);

      if (parsedList.length > 0) {
        const primaryReq = parsedList[0];
        setActiveRequestData(primaryReq);

        // Detect if request specifies its active module, otherwise use selectedModule
        const detectedMod: ModuleType =
          primaryReq.module?.toUpperCase() ||
          primaryReq.currentModule?.toUpperCase() ||
          (primaryReq.occupationDetails?.status?.toUpperCase() === "PENDING"
            ? "OCCUPATION"
            : primaryReq.willingnessDetails?.status?.toUpperCase() === "PENDING"
              ? "WILLINGNESS"
              : primaryReq.vacationDetails?.status?.toUpperCase() === "PENDING"
                ? "VACATION"
                : mod);

        setSelectedModule(detectedMod);
        extractCurrentApprover(primaryReq, detectedMod);
        toast.success(`Found ${parsedList.length} ongoing request(s) for Employee ${targetEmpId}`);
      } else {
        toast.info(`No pending ongoing requests found for Employee ID: ${targetEmpId}`);
      }
    } catch (err: any) {
      const msg = typeof err === "string" ? err : err?.message || "Failed to fetch pending requests.";
      toast.error(msg);
    } finally {
      setFetchingRequest(false);
    }
  };

  // Module change handler (re-evaluates current approver for the active request)
  const handleModuleSelect = (mod: ModuleType) => {
    setSelectedModule(mod);
    if (activeRequestData) {
      extractCurrentApprover(activeRequestData, mod);
    }
  };

  // Switch between multiple pending requests if returned
  const handleSelectRequestItem = (reqItem: any) => {
    setActiveRequestData(reqItem);
    const detectedMod: ModuleType =
      reqItem.module?.toUpperCase() ||
      reqItem.currentModule?.toUpperCase() ||
      (reqItem.occupationDetails?.status?.toUpperCase() === "PENDING"
        ? "OCCUPATION"
        : reqItem.willingnessDetails?.status?.toUpperCase() === "PENDING"
          ? "WILLINGNESS"
          : reqItem.vacationDetails?.status?.toUpperCase() === "PENDING"
            ? "VACATION"
            : selectedModule);

    setSelectedModule(detectedMod);
    extractCurrentApprover(reqItem, detectedMod);
  };

  // Extract current pending approver and timeline based on active module
  const extractCurrentApprover = (data: any, mod: ModuleType) => {
    let approvers: ApproverInfo[] = [];

    // 1. Direct array from current-pending-requests API
    const directApprovers =
      Array.isArray(data?.approvers) && data.approvers.length > 0
        ? data.approvers
        : Array.isArray(data?.approvalStages) && data.approvalStages.length > 0
          ? data.approvalStages
          : null;

    if (directApprovers) {
      approvers = directApprovers.map((appr: any, idx: number) => ({
        empId: String(appr.approverId || appr.empId || appr.employeeNumber || "").trim(),
        name: appr.approverName || appr.name || appr.employeeName || `Approver #${idx + 1}`,
        designation: appr.designation || appr.designationName || "",
        role: appr.role || "",
        status: appr.status || "PENDING",
        stage: appr.stage || idx + 1,
        approvalId: appr.approvalId || appr.id,
        isAssigned:
          appr.isAssigned === "1" ||
          appr.isAssigned === 1 ||
          appr.isAssigned === true ||
          appr.isAssigned === "true",
        approvedAt: appr.approvedAt || appr.updatedAt,
        remarks: appr.remarks || "",
      }));
    } else if (mod === "REGISTRATION") {
      const list = data?.approverDetails || data?.approvers || [];
      approvers = list.map((appr: any, idx: number) => ({
        empId: String(appr.empId || appr.employeeNumber || appr.approverId || "").trim(),
        name: appr.name || appr.employeeName || `Approver #${idx + 1}`,
        designation: appr.designation || appr.designationName || "",
        status: appr.status || "PENDING",
        stage: appr.stage || idx + 1,
        isAssigned: appr.isAssigned === true || appr.isAssigned === "true" || appr.isAssigned === "1" || appr.isAssigned === 1,
        approvedAt: appr.approvedAt || appr.updatedAt,
        remarks: appr.remarks || "",
      }));
    } else if (mod === "WILLINGNESS") {
      const wd = Array.isArray(data?.willingnessDetails) ? data.willingnessDetails[0] : data?.willingnessDetails || {};
      const list = wd?.approvers || wd?.willingnessApprovers || data?.willingnessApprovers || [];
      approvers = list.map((appr: any, idx: number) => ({
        empId: String(appr.empId || appr.employeeNumber || appr.approverId || "").trim(),
        name: appr.name || appr.employeeName || `Approver #${idx + 1}`,
        designation: appr.designation || appr.designationName || "",
        status: appr.status || "PENDING",
        stage: appr.stage || idx + 1,
        isAssigned: appr.isAssigned === true || appr.isAssigned === "true" || appr.isAssigned === "1" || appr.isAssigned === 1,
        approvedAt: appr.approvedAt || appr.updatedAt,
        remarks: appr.remarks || "",
      }));
    } else if (mod === "OCCUPATION") {
      const list = data?.occupationDetails?.approvals || data?.occupationDetails?.approvers || [];
      approvers = list.map((appr: any, idx: number) => ({
        empId: String(appr.empId || appr.employeeNumber || appr.approverId || "").trim(),
        name: appr.name || appr.employeeName || `Approver #${idx + 1}`,
        designation: appr.designation || appr.designationName || "",
        status: appr.status || "PENDING",
        stage: appr.stage || idx + 1,
        isAssigned: appr.isAssigned === true || appr.isAssigned === "true" || appr.isAssigned === "1" || appr.isAssigned === 1,
        approvedAt: appr.approvedAt || appr.updatedAt,
        remarks: appr.remarks || "",
      }));
    } else if (mod === "VACATION") {
      const list = data?.vacationDetails?.approvals || data?.vacationApprovers || data?.vacations?.[0]?.approvals || [];
      approvers = list.map((appr: any, idx: number) => ({
        empId: String(appr.empId || appr.employeeNumber || appr.approverId || "").trim(),
        name: appr.name || appr.employeeName || `Approver #${idx + 1}`,
        designation: appr.designation || appr.designationName || "",
        status: appr.status || "PENDING",
        stage: appr.stage || idx + 1,
        isAssigned: appr.isAssigned === true || appr.isAssigned === "true" || appr.isAssigned === "1" || appr.isAssigned === 1,
        approvedAt: appr.approvedAt || appr.updatedAt,
        remarks: appr.remarks || "",
      }));
    }

    setAllApproversList(approvers);

    // Find the currently assigned / pending approver
    if (data?.currentApprover && data.currentApprover.empId) {
      const ca = data.currentApprover;
      const matched = approvers.find((a) => padEmpNo(a.empId) === padEmpNo(ca.empId));
      setCurrentApprover({
        empId: String(ca.empId).trim(),
        name: ca.name || matched?.name || "Assigned Approver",
        designation: ca.designation || matched?.designation || "",
        status: data.status || matched?.status || "PENDING",
        stage: matched?.stage || 1,
        isAssigned: true,
        approvedAt: matched?.approvedAt,
      });
    } else {
      const pendingApprover =
        approvers.find((a) => a.isAssigned && (!a.status || a.status.toUpperCase() === "PENDING")) ||
        approvers.find((a) => !a.status || a.status.toUpperCase() === "PENDING" || a.status.toUpperCase() === "SUBMITTED") ||
        approvers[0] ||
        null;

      setCurrentApprover(pendingApprover);
    }
  };

  // Search Employees for new approver selection
  const handleNewApproverSearch = (query: string) => {
    if (newApproverSearchTimeoutRef.current) {
      clearTimeout(newApproverSearchTimeoutRef.current);
    }

    if (!query || query.trim().length < 2) return;

    newApproverSearchTimeoutRef.current = setTimeout(async () => {
      setSearchingNewApprover(true);
      try {
        const res = await dispatch(
          getEmployees({
            page: 1,
            limit: 20,
            search: query.trim(),
            query: query.trim(),
          })
        ).unwrap();

        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : [];

        const formatted = list.map((emp: any) => {
          const empNo = padEmpNo(emp.emp_no || emp.employeeNumber || emp.empId || emp.id);
          const name = emp.emp_name || emp.name || emp.fullName || [emp.firstname, emp.secondname, emp.lastname].filter(Boolean).join(" ");
          const desig = emp.designation_name || emp.designation || emp.desig || "";
          const dept = emp.department_name || emp.department || "";

          return {
            value: empNo,
            label: `${empNo} - ${name} ${desig ? `(${desig})` : ""}`,
            empNo,
            name,
            designation: desig,
            department: dept,
          };
        });

        setNewApproverOptions(formatted);
      } catch (e) {
        console.error("New approver search error", e);
      } finally {
        setSearchingNewApprover(false);
      }
    }, 300);
  };

  // When a new approver is selected or entered
  const handleSelectNewApprover = async (empNoVal: string) => {
    const padded = padEmpNo(empNoVal);
    setNewApproverEmpId(padded);

    const foundInOptions = newApproverOptions.find((o) => o.value === padded);
    if (foundInOptions) {
      setNewApproverDetails(foundInOptions);
      return;
    }

    // Lookup profile if typed directly
    if (padded && padded.length >= 2) {
      try {
        const profileRes = await dispatch(getEmployeeProfile({ empId: padded })).unwrap();
        const p = profileRes?.data || profileRes;
        const pd = p?.personal_details || p || {};
        const name = pd.emp_name || pd.fullName || [pd.firstname, pd.secondname, pd.lastname].filter(Boolean).join(" ");
        const desig = p?.designation_name || p?.designation || pd.designation || "";
        const dept = p?.department_name || p?.department || "";

        setNewApproverDetails({
          empNo: padded,
          name: name || `Employee #${padded}`,
          designation: desig || "Officer",
          department: dept,
        });
      } catch (err) {
        setNewApproverDetails({
          empNo: padded,
          name: `Employee #${padded}`,
          designation: "Officer",
        });
      }
    }
  };

  // Validation before opening confirm modal
  const handleInitiateUpdate = () => {
    if (!activeRequestData) {
      toast.warning("Please search and select an ongoing request first.");
      return;
    }

    if (!newApproverEmpId || !newApproverEmpId.trim()) {
      toast.warning("Please select or enter the New Approver's Employee ID.");
      return;
    }

    const cleanNewApprover = padEmpNo(newApproverEmpId);
    const cleanCurrentApprover = padEmpNo(currentApprover?.empId);

    if (cleanNewApprover === cleanCurrentApprover) {
      toast.error("The New Approver cannot be the same as the Current Approver.");
      return;
    }

    if (!remarks || !remarks.trim()) {
      toast.warning("Please provide a reason / remark for changing the approver.");
      return;
    }

    setConfirmModalOpen(true);
  };

  // Final submit handler
  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      const regId = activeRequestData?.registrationId || activeRequestData?.id;
      const cleanNewEmp = padEmpNo(newApproverEmpId);

      const payload: any = {
        module: selectedModule,
        registrationId: Number(regId),
        newApproverEmpId: cleanNewEmp,
        remarks: remarks.trim(),
      };

      if (selectedModule === "WILLINGNESS") {
        if (activeRequestData?.willingnessId) {
          payload.willingnessId = Number(activeRequestData.willingnessId);
        }
      } else if (selectedModule === "OCCUPATION") {
        if (activeRequestData?.occupationId || activeRequestData?.occupationDetails?.id) {
          payload.occupationId = Number(activeRequestData.occupationId || activeRequestData.occupationDetails?.id);
        }
      } else if (selectedModule === "VACATION") {
        if (activeRequestData?.vacationId || activeRequestData?.vacationDetails?.id) {
          payload.vacationId = Number(activeRequestData.vacationId || activeRequestData.vacationDetails?.id);
        }
      }

      const res = await dispatch(updateStaffQuarterCurrentApprover(payload)).unwrap();
      const resData = res?.data || res;

      if (res?.success !== false && resData?.success !== false) {
        toast.success(`Current approver updated successfully to ${cleanNewEmp}!`);
        setConfirmModalOpen(false);
        setRemarks("");
        // Re-fetch pending requests to refresh status
        handleFetchPendingRequests(searchEmpId, selectedModule);
      } else {
        toast.error(res?.message || resData?.message || "Failed to update current approver.");
      }
    } catch (err: any) {
      const msg = typeof err === "string" ? err : err?.message || "An error occurred while updating the approver.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setSearchEmpId("");
    setRawApiResponse(null);
    setAvailableRequestsList([]);
    setActiveRequestData(null);
    setCurrentApprover(null);
    setAllApproversList([]);
    setNewApproverEmpId("");
    setNewApproverDetails(null);
    setRemarks("");
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1 fw-bold text-dark d-flex align-items-center gap-2">
              <i className="ti ti-user-shield text-danger fs-3"></i> Change Current Approver
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.staffQuarterAdmin?.dashboard || routes.staffQuarter?.overview || "#"}>
                    Staff Quarters
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.settings.staffQuarterApproval || "#"}>Approval Settings</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Change Approver
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link
              to={routes.settings.staffQuarterApproval || "#"}
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 shadow-sm"
              style={{ borderRadius: "8px" }}
            >
              <i className="ti ti-settings me-1"></i> Approval Settings
            </Link>
            <button
              type="button"
              className="btn btn-light btn-sm d-flex align-items-center gap-1 border shadow-sm"
              onClick={handleReset}
              style={{ borderRadius: "8px" }}
            >
              <i className="ti ti-refresh me-1"></i> Reset Form
            </button>
          </div>
        </div>

        {/* Step 1: Search Pending Requests by Employee ID */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "14px" }}>
          <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle text-danger"
                style={{ width: "32px", height: "32px" }}
              >
                <span className="fw-bold" style={{ fontSize: "14px" }}>1</span>
              </div>
              <div>
                <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "15px" }}>
                  Search Ongoing Requests by Employee ID
                </h5>
                <span className="text-muted extra-small">
                  Fetch active workflows and approvers pipeline using applicant's Employee ID
                </span>
              </div>
            </div>
            <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2">
              HR Admin Authorized
            </span>
          </div>

          <div className="card-body p-4">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label small fw-bold text-dark mb-1">
                  Applicant Employee ID <span className="text-danger">*</span>
                </label>
                <div className="d-flex flex-md-row flex-column gap-2 align-items-stretch">
                  <div className="input-group flex-grow-1">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="ti ti-user-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Employee ID (e.g. 00010212, 00019195)..."
                      value={searchEmpId}
                      onChange={(e) => setSearchEmpId(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleFetchPendingRequests();
                        }
                      }}
                      style={{ height: "42px" }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-danger d-flex align-items-center justify-content-center gap-2 shadow-sm flex-shrink-0 px-4"
                    onClick={() => handleFetchPendingRequests()}
                    disabled={fetchingRequest || !searchEmpId.trim()}
                    style={{ height: "42px", fontWeight: 600, borderRadius: "8px", minWidth: "220px" }}
                  >
                    {fetchingRequest ? (
                      <>
                        <Spin size="small" /> Searching...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-search"></i> Search Pending Requests
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Spinner */}
        {fetchingRequest && (
          <div className="text-center py-5">
            <Spin size="large" />
            <div className="text-muted mt-2 fw-semibold">Fetching ongoing pending requests & approvers...</div>
          </div>
        )}

        {/* Step 2: Requests & Approvers Pipeline */}
        {availableRequestsList.length > 0 && !fetchingRequest && (
          <>
            {/* Multiple Requests Tabs / Cards */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "14px" }}>
              <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <i className="ti ti-files text-primary fs-4"></i>
                  <h6 className="mb-0 fw-bold text-dark">
                    Ongoing Workflow Requests ({availableRequestsList.length}) for Employee {padEmpNo(searchEmpId)}
                  </h6>
                </div>
                <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                  Select a request to view & manage approvers
                </span>
              </div>

              <div className="card-body p-3">
                <div className="row g-3">
                  {availableRequestsList.map((req, idx) => {
                    const isCur =
                      (req.vacationId && activeRequestData?.vacationId === req.vacationId) ||
                      (req.vacationIntimationId && activeRequestData?.vacationIntimationId === req.vacationIntimationId) ||
                      (req.id && activeRequestData?.id === req.id) ||
                      (req.registrationId && activeRequestData?.registrationId === req.registrationId && idx === 0);

                    const modName = req.module || "WORKFLOW";
                    const statusText = req.status || "PENDING";
                    const isPending = statusText.toUpperCase() === "PENDING";
                    const isIntimated = statusText.toUpperCase() === "INTIMATED";
                    const isApproved = statusText.toUpperCase() === "APPROVED";

                    const apprCount = (req.approvers || req.approvalStages || []).length;
                    const curApprName = req.currentApprover?.name || null;

                    return (
                      <div key={idx} className="col-lg-6 col-12">
                        <div
                          onClick={() => handleSelectRequestItem(req)}
                          style={{
                            cursor: "pointer",
                            borderRadius: "12px",
                            border: `2px solid ${isCur ? "#E30613" : "#e2e8f0"}`,
                            background: isCur ? "#fff9f9" : "#fff",
                            boxShadow: isCur ? "0 4px 14px rgba(227, 6, 19, 0.12)" : "0 1px 3px rgba(0,0,0,0.05)",
                            transition: "all 0.2s ease-in-out",
                          }}
                          className="p-3 h-100"
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className={`badge ${modName === "VACATION"
                                  ? "bg-warning-subtle text-warning-emphasis border border-warning"
                                  : modName === "REGISTRATION"
                                    ? "bg-primary-subtle text-primary border border-primary"
                                    : modName === "WILLINGNESS"
                                      ? "bg-info-subtle text-info border border-info"
                                      : "bg-success-subtle text-success border border-success"
                                  } px-2 py-1`}
                                style={{ fontSize: "11px", fontWeight: 700 }}
                              >
                                {modName}
                              </span>
                              <span className="fw-bold text-dark small">
                                {req.vacationIntimationId ||
                                  (req.vacationId ? `Vacation #${req.vacationId}` : null) ||
                                  (req.registrationId ? `Reg #${req.registrationId}` : null) ||
                                  `Request #${idx + 1}`}
                              </span>
                            </div>

                            <span
                              className={`badge ${isPending
                                ? "bg-warning text-dark"
                                : isIntimated
                                  ? "bg-info text-white"
                                  : isApproved
                                    ? "bg-success text-white"
                                    : "bg-secondary text-white"
                                }`}
                              style={{ fontSize: "10.5px" }}
                            >
                              {statusText}
                            </span>
                          </div>

                          {/* Quarter / Flat Info */}
                          <div className="text-secondary small mb-2 d-flex flex-wrap gap-2">
                            {req.details?.locationName && (
                              <span>
                                <i className="ti ti-map-pin me-1 text-danger"></i>
                                {req.details.locationName}
                              </span>
                            )}
                            {req.details?.quarterType && (
                              <span>
                                <i className="ti ti-home me-1 text-muted"></i>
                                Type {req.details.quarterType}
                              </span>
                            )}
                            {req.details?.flatNumber && (
                              <span>
                                <i className="ti ti-door me-1 text-muted"></i>
                                Flat {req.details.flatNumber}
                              </span>
                            )}
                          </div>

                          {/* Approver Summary */}
                          <div
                            className="p-2 rounded-2 d-flex align-items-center justify-content-between mt-2"
                            style={{
                              background: isCur ? "#fee2e2" : "#f8fafc",
                              border: "1px solid #e2e8f0",
                              fontSize: "12px",
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 text-truncate">
                              <i className={`ti ${curApprName ? "ti-user-check text-success" : "ti-clock text-muted"} fs-5`}></i>
                              <span className="text-truncate">
                                {curApprName ? (
                                  <>
                                    <strong>Pending with:</strong> {curApprName}
                                  </>
                                ) : (
                                  <span className="text-muted">No pending approver assigned</span>
                                )}
                              </span>
                            </div>
                            <span className="badge bg-white text-dark border ms-2">
                              {apprCount} {apprCount === 1 ? "Stage" : "Stages"}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Request Summary & Current Approver Card */}
            {activeRequestData && (
              <div className="row g-4 mb-4">
                {/* Applicant & Request Overview */}
                <div className="col-lg-5 col-12">
                  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "14px" }}>
                    <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
                          style={{ width: "30px", height: "30px" }}
                        >
                          <i className="ti ti-file-info fs-5"></i>
                        </div>
                        <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "15px" }}>
                          Request Details ({selectedModule})
                        </h5>
                      </div>
                      <span
                        className={`badge ${activeRequestData.status?.toUpperCase() === "APPROVED"
                          ? "bg-success"
                          : activeRequestData.status?.toUpperCase() === "REJECTED"
                            ? "bg-danger"
                            : activeRequestData.status?.toUpperCase() === "INTIMATED"
                              ? "bg-info text-white"
                              : "bg-warning text-dark"
                          }`}
                      >
                        {activeRequestData.status || "PENDING"}
                      </span>
                    </div>

                    <div className="card-body p-3">
                      <div className="p-3 bg-light-subtle rounded-3 border mb-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm flex-shrink-0"
                            style={{ width: "46px", height: "46px", border: "1px solid #e2e8f0" }}
                          >
                            <i className="ti ti-user text-danger fs-3"></i>
                          </div>
                          <div>
                            <h6 className="mb-0 fw-bold text-dark">
                              Applicant Emp ID: {padEmpNo(searchEmpId)}
                            </h6>
                            <span className="text-muted small d-block">
                              Workflow Module: <strong>{selectedModule}</strong> &middot; ID: #
                              {activeRequestData.vacationIntimationId ||
                                activeRequestData.vacationId ||
                                activeRequestData.registrationId ||
                                activeRequestData.id ||
                                "-"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="row g-3">
                        <div className="col-6">
                          <span className="text-muted extra-small d-block" style={{ fontSize: "11px" }}>
                            Registration ID
                          </span>
                          <span className="fw-bold text-dark">
                            #{activeRequestData.registrationId || activeRequestData.id || "-"}
                          </span>
                        </div>

                        <div className="col-6">
                          <span className="text-muted extra-small d-block" style={{ fontSize: "11px" }}>
                            Intimation / Ref No
                          </span>
                          <span className="fw-bold text-dark">
                            {activeRequestData.vacationIntimationId ||
                              activeRequestData.referenceNumber ||
                              activeRequestData.registrationNumber ||
                              "-"}
                          </span>
                        </div>

                        <div className="col-6">
                          <span className="text-muted extra-small d-block" style={{ fontSize: "11px" }}>
                            Quarter Type
                          </span>
                          <span className="fw-bold text-dark">
                            {activeRequestData.details?.quarterType ||
                              activeRequestData.quarterTypeEligibility?.quarterType ||
                              activeRequestData.quarterType ||
                              "-"}
                          </span>
                        </div>

                        <div className="col-6">
                          <span className="text-muted extra-small d-block" style={{ fontSize: "11px" }}>
                            Location & Flat
                          </span>
                          <span className="fw-bold text-danger">
                            {activeRequestData.details?.locationName
                              ? `${activeRequestData.details.locationName} - Flat ${activeRequestData.details.flatNumber || ""
                              }`
                              : formatFlatNoWithBlock(
                                activeRequestData.allotmentDetails ||
                                activeRequestData.willingnessDetails ||
                                activeRequestData,
                                activeRequestData.block,
                                activeRequestData
                              )}
                          </span>
                        </div>

                        <div className="col-6">
                          <span className="text-muted extra-small d-block" style={{ fontSize: "11px" }}>
                            Creation / Intimation Date
                          </span>
                          <span className="fw-bold text-dark">
                            {formatDate(
                              activeRequestData.details?.vacationIntimationDate ||
                              activeRequestData.createdAt ||
                              activeRequestData.applicationDate
                            )}
                          </span>
                        </div>

                        <div className="col-6">
                          <span className="text-muted extra-small d-block" style={{ fontSize: "11px" }}>
                            Reason
                          </span>
                          <span className="fw-bold text-dark">
                            {activeRequestData.details?.reasonForVacation ||
                              activeRequestData.reason ||
                              "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Current Pending Approver & Flow */}
                <div className="col-lg-7 col-12">
                  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "14px" }}>
                    <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle text-warning"
                          style={{ width: "30px", height: "30px" }}
                        >
                          <i className="ti ti-user-check fs-5"></i>
                        </div>
                        <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "15px" }}>
                          Approvers Data & Workflow Stages
                        </h5>
                      </div>
                      <span className="badge bg-dark-subtle text-dark">
                        {allApproversList.length} {allApproversList.length === 1 ? "Approver" : "Approvers"}
                      </span>
                    </div>

                    <div className="card-body p-3">
                      {/* Active Pending Approver Spotlight */}
                      {currentApprover ? (
                        <div
                          className="p-3 rounded-3 border mb-3"
                          style={{
                            backgroundColor: "#fffdfa",
                            borderColor: "#fde68a",
                            borderLeft: "5px solid #f59e0b",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="badge bg-warning text-dark fw-bold" style={{ fontSize: "10px" }}>
                              ACTIVE PENDING APPROVER (STAGE {currentApprover.stage || 1})
                            </span>
                            <span className="badge bg-warning-subtle text-warning border border-warning-subtle">
                              Action Pending
                            </span>
                          </div>

                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="bg-white rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                              style={{ width: "44px", height: "44px", border: "1px solid #fde68a" }}
                            >
                              <i className="ti ti-user-exclamation text-warning fs-3"></i>
                            </div>
                            <div>
                              <div className="fw-bold text-dark fs-6">{currentApprover.name}</div>
                              <div className="text-muted small">
                                Emp ID: <strong className="text-dark">{padEmpNo(currentApprover.empId)}</strong>
                                {currentApprover.designation && ` · ${currentApprover.designation}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="alert alert-info border-0 rounded-3 mb-3 d-flex align-items-center gap-2">
                          <i className="ti ti-info-circle fs-5"></i>
                          <span>No pending approver currently assigned to this stage.</span>
                        </div>
                      )}

                      {/* Detailed Approvers Stages List */}
                      {allApproversList.length > 0 ? (
                        <div className="mt-3">
                          <label className="text-muted extra-small fw-bold text-uppercase d-block mb-2">
                            Approval Pipeline Stages ({allApproversList.length})
                          </label>
                          <div className="d-flex flex-column gap-2" style={{ maxHeight: "260px", overflowY: "auto" }}>
                            {allApproversList.map((appr, idx) => {
                              const isCurrent =
                                appr.isAssigned ||
                                padEmpNo(appr.empId) === padEmpNo(currentApprover?.empId);
                              const isApproved = appr.status?.toUpperCase() === "APPROVED";
                              const isRejected = appr.status?.toUpperCase() === "REJECTED";
                              const isPending = !isApproved && !isRejected;

                              return (
                                <div
                                  key={idx}
                                  className={`p-3 rounded-3 border d-flex flex-column gap-1 ${isCurrent
                                    ? "border-warning bg-warning-subtle"
                                    : isApproved
                                      ? "border-success-subtle bg-success-subtle"
                                      : "bg-white"
                                    }`}
                                >
                                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        className={`badge ${isApproved
                                          ? "bg-success text-white"
                                          : isCurrent
                                            ? "bg-warning text-dark"
                                            : "bg-light text-dark border"
                                          }`}
                                      >
                                        Stage {appr.stage || idx + 1}
                                      </span>
                                      <div>
                                        <span className="fw-bold text-dark">{appr.name}</span>
                                        <span className="text-muted ms-1 small">
                                          (Emp ID: {padEmpNo(appr.empId)})
                                        </span>
                                      </div>
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                      {isCurrent && (
                                        <span className="badge bg-warning text-dark fw-bold" style={{ fontSize: "10px" }}>
                                          Currently Assigned
                                        </span>
                                      )}
                                      <span
                                        className={`badge ${isApproved
                                          ? "bg-success text-white"
                                          : isRejected
                                            ? "bg-danger text-white"
                                            : isCurrent
                                              ? "bg-warning-subtle text-dark border border-warning"
                                              : "bg-light text-muted border"
                                          }`}
                                        style={{ fontSize: "11px" }}
                                      >
                                        {isApproved ? "Approved" : isRejected ? "Rejected" : "Pending"}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-center justify-content-between text-muted extra-small mt-1">
                                    <span>
                                      {appr.designation ? `Designation: ${appr.designation}` : ""}
                                      {appr.role ? ` · Role: ${appr.role}` : ""}
                                    </span>
                                    {appr.approvedAt && (
                                      <span>
                                        <i className="ti ti-calendar me-1"></i>
                                        {formatDateTime(appr.approvedAt)}
                                      </span>
                                    )}
                                  </div>

                                  {appr.remarks && (
                                    <div className="text-secondary small mt-1 bg-white p-2 rounded border">
                                      <strong>Remarks:</strong> {appr.remarks}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted text-center py-3">
                          No approver stages configured for this request yet.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Reassign to New Approver Form */}
            {activeRequestData && (
              <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "14px" }}>
                <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle bg-success-subtle text-success"
                      style={{ width: "30px", height: "30px" }}
                    >
                      <span className="fw-bold" style={{ fontSize: "13px" }}>3</span>
                    </div>
                    <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "15px" }}>
                      Reassign Approver & Justification
                    </h5>
                  </div>
                  <span className="text-muted small">
                    Assign a replacement officer for the active {selectedModule} request
                  </span>
                </div>

                <div className="card-body p-4">
                  <div className="row g-4">
                    {/* Search / Select New Approver */}
                    <div className="col-lg-6 col-12">
                      <label className="form-label small fw-bold text-dark mb-1">
                        New Approver Employee ID <span className="text-danger">*</span>
                      </label>
                      <div className="input-group mb-2">
                        <span className="input-group-text bg-light border-end-0">
                          <i className="ti ti-user-plus text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter Employee ID (e.g. 7699, 5418)..."
                          value={newApproverEmpId}
                          onChange={(e) => handleSelectNewApprover(e.target.value)}
                          style={{ height: "42px" }}
                        />
                      </div>
                      <div className="form-text extra-small text-muted">
                        Enter Employee ID of the officer who should review and approve this stage.
                      </div>

                      {/* Selected New Approver Profile Card */}
                      {newApproverDetails && (
                        <div
                          className="p-3 mt-3 rounded-3 border d-flex align-items-center gap-3"
                          style={{
                            backgroundColor: "#f0fdf4",
                            borderColor: "#bbf7d0",
                            borderLeft: "4px solid #22c55e",
                          }}
                        >
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm flex-shrink-0"
                            style={{ width: "42px", height: "42px", border: "1px solid #86efac" }}
                          >
                            <i className="ti ti-user-check text-success fs-4"></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark">{newApproverDetails.name}</div>
                            <div className="text-muted small">
                              Emp ID: <strong className="text-success">{newApproverDetails.empNo}</strong>
                              {newApproverDetails.designation && ` · ${newApproverDetails.designation}`}
                              {newApproverDetails.department && ` · ${newApproverDetails.department}`}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Reason for Change */}
                    <div className="col-lg-6 col-12">
                      <label className="form-label small fw-bold text-dark mb-1">
                        Reason for Changing Approver <span className="text-danger">*</span>
                      </label>
                      <Input.TextArea
                        rows={3}
                        placeholder="Explain the reason for reassigning the current approver..."
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        style={{ borderRadius: "8px" }}
                      />

                      {/* Preset Suggestions */}
                      <div className="mt-2">
                        <span className="text-muted extra-small d-block mb-1">Quick Select Reason:</span>
                        <div className="d-flex flex-wrap gap-1">
                          {remarkPresets.map((preset, pIdx) => (
                            <button
                              key={pIdx}
                              type="button"
                              className="btn btn-outline-secondary btn-sm py-0 px-2"
                              style={{ fontSize: "11px", borderRadius: "12px" }}
                              onClick={() => setRemarks(preset)}
                            >
                              {preset}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex align-items-center justify-content-end gap-2 mt-4 pt-3 border-top">
                    <button
                      type="button"
                      className="btn btn-light border px-4"
                      onClick={handleReset}
                      style={{ minWidth: "120px", borderRadius: "8px" }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger px-4 shadow-sm"
                      onClick={handleInitiateUpdate}
                      disabled={!newApproverEmpId.trim() || !remarks.trim() || submitting}
                      style={{ minWidth: "160px", fontWeight: 600, borderRadius: "8px" }}
                    >
                      <i className="ti ti-check me-1"></i> Update Approver
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Confirmation Modal */}
        <Modal
          title={
            <div className="d-flex align-items-center gap-2">
              <i className="ti ti-alert-triangle text-warning fs-4"></i>
              <span>Confirm Approver Reassignment</span>
            </div>
          }
          open={confirmModalOpen}
          onCancel={() => setConfirmModalOpen(false)}
          footer={[
            <button
              key="back"
              type="button"
              className="btn btn-light border"
              onClick={() => setConfirmModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </button>,
            <button
              key="submit"
              type="button"
              className="btn btn-danger px-4"
              onClick={handleConfirmSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status"></span> Updating...
                </>
              ) : (
                "Confirm & Update"
              )}
            </button>,
          ]}
        >
          <div className="py-2">
            <p className="text-secondary mb-3">
              Are you sure you want to change the current approver for this <strong>{selectedModule}</strong> request?
            </p>

            <div className="p-3 bg-light rounded-3 border mb-3">
              <div className="row g-2 small">
                <div className="col-5 text-muted">Module:</div>
                <div className="col-7 fw-bold text-dark">{selectedModule}</div>

                <div className="col-5 text-muted">Applicant Emp ID:</div>
                <div className="col-7 fw-bold text-dark">{padEmpNo(searchEmpId)}</div>

                <div className="col-5 text-muted">Request / Vacation ID:</div>
                <div className="col-7 fw-bold text-dark">
                  #{activeRequestData?.vacationIntimationId ||
                    activeRequestData?.vacationId ||
                    activeRequestData?.registrationId ||
                    activeRequestData?.id}
                </div>

                <div className="col-5 text-muted">Current Approver:</div>
                <div className="col-7 fw-bold text-danger">
                  {currentApprover?.name || "Current Officer"} ({padEmpNo(currentApprover?.empId)})
                </div>

                <div className="col-5 text-muted">New Approver:</div>
                <div className="col-7 fw-bold text-success">
                  {newApproverDetails?.name || newApproverEmpId} ({padEmpNo(newApproverEmpId)})
                </div>

                <div className="col-5 text-muted">Reason / Remarks:</div>
                <div className="col-7 text-dark">{remarks}</div>
              </div>
            </div>

            <div
              className="alert alert-warning mb-0 border-0 p-2 d-flex align-items-center gap-2"
              style={{ fontSize: "12px" }}
            >
              <i className="ti ti-info-circle text-warning fs-5"></i>
              <div>The pending task will be reassigned immediately to the new approver's inbox.</div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ChangeCurrentApprover;

